import { useEffect, useState } from "react"
import { adminApi } from "../../services/api"

function toBadgeTone (isApproved) {
  return isApproved ? "badge-success" : "badge-ghost"
}

const CATEGORY_OPTIONS = ['Web Development', 'Mobile Development', 'AI/ML', 'IoT', 'Blockchain', 'Data Science', 'Other']
const DIFFICULTY_OPTIONS = ['Beginner', 'Intermediate', 'Advanced']

export default function AdminIdeas ({ students = [] }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [topics, setTopics] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 })

  const [search, setSearch] = useState("")
  const [studentId, setStudentId] = useState("")
  const [category, setCategory] = useState("")
  const [difficulty, setDifficulty] = useState("")
  const [approval, setApproval] = useState("") // "approved" | "pending" | ""

  const [page, setPage] = useState(1)
  const limit = 20

  const [topicModalOpen, setTopicModalOpen] = useState(false)
  const [selectedTopic, setSelectedTopic] = useState(null)

  const clearFilters = () => {
    const nextFilters = { search: "", studentId: "", category: "", difficulty: "", approval: "" }
    setSearch(nextFilters.search)
    setStudentId(nextFilters.studentId)
    setCategory(nextFilters.category)
    setDifficulty(nextFilters.difficulty)
    setApproval(nextFilters.approval)
    setPage(1)
    fetchTopics(1, nextFilters)
  }

  const fetchTopics = async (nextPage = page, overrides = {}) => {
    const effectiveSearch = overrides.search ?? search
    const effectiveStudentId = overrides.studentId ?? studentId
    const effectiveCategory = overrides.category ?? category
    const effectiveDifficulty = overrides.difficulty ?? difficulty
    const effectiveApproval = overrides.approval ?? approval

    setLoading(true)
    setError("")
    try {
      const params = {
        page: nextPage,
        limit
      }

      if (effectiveStudentId) params.studentId = effectiveStudentId
      if (effectiveSearch.trim()) params.search = effectiveSearch.trim()
      if (effectiveCategory) params.category = effectiveCategory
      if (effectiveDifficulty) params.difficulty = effectiveDifficulty
      if (effectiveApproval) params.isApproved = effectiveApproval === "approved" ? "true" : "false"

      const res = await adminApi.ideas.list(params)
      const data = res?.data?.data
      setTopics(data?.topics || [])
      if (data?.pagination) setPagination(data.pagination)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load ideas")
      setTopics([])
      setPagination({ page: nextPage, limit, total: 0, pages: 1 })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTopics(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = () => {
    setPage(1)
    fetchTopics(1)
  }

  const openTopic = (t) => {
    setSelectedTopic(t)
    setTopicModalOpen(true)
  }

  return (
    <div>
      <div className="flex gap-2 justify-end mb-3">
        <button className="btn btn-ghost btn-sm" type="button" onClick={clearFilters} disabled={loading}>
          Clear
        </button>
        <button className="btn btn-primary btn-sm" type="button" onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="label">Search</label>
          <input
            type="text"
            className="input input-bordered w-full input-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, summary, keywords..."
          />
        </div>

        <div className="sm:w-56">
          <label className="label">Student</label>
          <select className="select select-bordered w-full select-sm" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            <option value="">All students</option>
            {students.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name} ({s.email})
              </option>
            ))}
          </select>
        </div>

        <div className="sm:w-44">
          <label className="label">Category</label>
          <select className="select select-bordered w-full select-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:w-44">
          <label className="label">Difficulty</label>
          <select className="select select-bordered w-full select-sm" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            <option value="">All</option>
            {DIFFICULTY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:w-40">
          <label className="label">Approval</label>
          <select className="select select-bordered w-full select-sm" value={approval} onChange={(e) => setApproval(e.target.value)}>
            <option value="">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {error && <p className="text-error mb-2">{error}</p>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="mb-2 text-sm text-slate-600">
            Showing {topics.length} result{topics.length === 1 ? "" : "s"}.
          </div>
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Student</th>
                <th>Title</th>
                <th>Category</th>
                <th>Difficulty</th>
                <th>Approved</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {topics.map((t) => (
                <tr key={t._id}>
                  <td>
                    <div className="font-medium">{t.createdBy?.name || "—"}</div>
                    <div className="text-xs text-slate-500">{t.createdBy?.email || "—"}</div>
                  </td>
                  <td>
                    <div className="font-semibold">{t.title}</div>
                    {t.summary ? <div className="text-sm text-slate-600 line-clamp-2">{t.summary}</div> : null}
                  </td>
                  <td>{t.category || "—"}</td>
                  <td>{t.difficulty || "—"}</td>
                  <td>
                    <span className={`badge badge-sm ${toBadgeTone(t.isApproved)}`}>{t.isApproved ? "Approved" : "Pending"}</span>
                  </td>
                  <td>{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "—"}</td>
                  <td>
                    <button className="btn btn-ghost btn-xs" type="button" onClick={() => openTopic(t)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {topics.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                      No ideas found for the current filters.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {pagination?.pages > 1 ? (
            <div className="flex items-center justify-between mt-3">
              <button
                className="btn btn-sm"
                type="button"
                disabled={page <= 1}
                onClick={() => {
                  const next = Math.max(1, page - 1)
                  setPage(next)
                  fetchTopics(next)
                }}
              >
                Prev
              </button>
              <div className="text-sm text-slate-600">
                Page {pagination.page} of {pagination.pages}
              </div>
              <button
                className="btn btn-sm"
                type="button"
                disabled={page >= pagination.pages}
                onClick={() => {
                  const next = page + 1
                  setPage(next)
                  fetchTopics(next)
                }}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}

      <dialog open={topicModalOpen} className="modal modal-bottom sm:modal-middle" onClick={() => setTopicModalOpen(false)}>
        <div className="modal-box max-w-3xl bg-base-100 text-base-content" onClick={(e) => e.stopPropagation()}>
          <h2 className="text-xl font-bold">Topic Details</h2>
          {selectedTopic ? (
            <div className="mt-4 space-y-3">
              <div className="space-y-1">
                <p className="font-semibold">{selectedTopic.title}</p>
                <p className="text-sm text-slate-600">{selectedTopic.description || selectedTopic.summary || "—"}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p>
                    <span className="font-semibold">Student:</span> {selectedTopic.createdBy?.name || "—"}
                  </p>
                  <p className="text-sm text-slate-600">{selectedTopic.createdBy?.email || ""}</p>
                </div>
                <div className="space-y-1">
                  <p>
                    <span className="font-semibold">Approval:</span>{" "}
                    {selectedTopic.isApproved ? (
                      <span className="badge badge-success badge-sm">Approved</span>
                    ) : (
                      <span className="badge badge-ghost badge-sm">Pending</span>
                    )}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedTopic.approvedBy ? `Approved by ${selectedTopic.approvedBy.name} (${selectedTopic.approvedBy.email})` : "—"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <p className="text-sm">
                  <span className="font-semibold">Category:</span> {selectedTopic.category || "—"}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Difficulty:</span> {selectedTopic.difficulty || "—"}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Created:</span>{" "}
                  {selectedTopic.createdAt ? new Date(selectedTopic.createdAt).toLocaleDateString() : "—"}
                </p>
              </div>

              {selectedTopic.explanation ? (
                <div className="border-t border-base-300 pt-3">
                  <p className="font-semibold mb-1">Explanation</p>
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {selectedTopic.explanation}
                  </div>
                </div>
              ) : null}

              {selectedTopic.features?.length ? (
                <div className="border-t border-base-300 pt-3">
                  <p className="font-semibold mb-1">Features</p>
                  <ul className="list-disc pl-5 text-sm space-y-1">
                    {selectedTopic.features.map((f, idx) => (
                      <li key={`${idx}-${f}`}>{f}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="mt-4 text-sm text-slate-600">Loading…</p>
          )}

          <div className="modal-action">
            <button className="btn" type="button" onClick={() => setTopicModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}

